import { lazy, Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/shared/components/layout';
import { AuthenticatedLayout } from '@/shared/components/layout/AuthenticatedLayout';
import { RequireRole } from '@/shared/components/auth/RequireRole';
import { PageLoader } from '@/shared/components/ui/PageLoader';

// ---------------------------------------------------------------------------
// Retry dynamic imports once with a full-page reload to handle stale chunk
// references after deploys (chunk-not-found errors).
// ---------------------------------------------------------------------------

function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem('chunk-reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        return new Promise<{ default: React.ComponentType }>(() => {});
      }
      sessionStorage.removeItem('chunk-reload');
      return importFn();
    })
  );
}

// ---------------------------------------------------------------------------
// Lazy page imports
// ---------------------------------------------------------------------------

const HomePage = lazyWithRetry(() => import('@/pages/HomePage'));
const SubmissionPage = lazyWithRetry(() => import('@/pages/SubmissionPage'));
const ConfirmationPage = lazyWithRetry(() => import('@/pages/ConfirmationPage'));
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const SubmissionsPage = lazyWithRetry(() => import('@/pages/SubmissionsPage'));
const SubmissionDetailPage = lazyWithRetry(() => import('@/pages/SubmissionDetailPage'));
const AdminPage = lazyWithRetry(() => import('@/pages/AdminPage'));
const IntakeFormPage = lazyWithRetry(() => import('@/pages/IntakeFormPage'));

// ---------------------------------------------------------------------------
// Suspense wrapper helper
// ---------------------------------------------------------------------------

function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Legacy /review/:id redirect — reads the id param and navigates to the new
// /submissions/:id route.
// ---------------------------------------------------------------------------

function ReviewIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/submissions/${id}`} replace />;
}

export const router = createBrowserRouter([
  // ── Public routes — AppLayout shell (vendor submission form + home) ──────
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: withSuspense(HomePage) },
      { path: 'submit', element: withSuspense(SubmissionPage) },
      { path: 'submitted', element: withSuspense(ConfirmationPage) },
      { path: '*', element: withSuspense(NotFoundPage) },
    ],
  },

  // ── Authenticated app routes — AuthenticatedLayout shell ─────────────────
  // RequireRole renders <Navigate to="/" replace /> when access is denied,
  // sending the user back to the HomePage to sign in.
  {
    path: '/',
    element: (
      <RequireRole fallback={<Navigate to="/" replace />}>
        <AuthenticatedLayout />
      </RequireRole>
    ),
    children: [
      { path: 'dashboard', element: withSuspense(DashboardPage) },
      { path: 'submissions', element: withSuspense(SubmissionsPage) },
      { path: 'submissions/:id', element: withSuspense(SubmissionDetailPage) },
      { path: 'admin', element: withSuspense(AdminPage) },
      { path: 'intake-form', element: withSuspense(IntakeFormPage) },
    ],
  },

  // ── Legacy /review redirects ─────────────────────────────────────────────
  { path: '/review', element: <Navigate to="/submissions" replace /> },
  { path: '/review/:id', element: <ReviewIdRedirect /> },
]);
